import React, { useState } from 'react';
import { RefreshCw, Package, Droplet, Droplets, Truck, MapPin, ChevronRight, Eye, Shirt, Calendar, Clock } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

// Formatea la fecha de alistamiento a algo legible en español.
// Acepta ISO string, Date o undefined; cae a createdAt si no llega fecha_alistamiento.
const formatFechaAlistamiento = (entrega) => {
  const iso = entrega?.fecha_alistamiento || entrega?.createdAt;
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return {
    fechaCorta: format(d, "dd 'de' MMM, yyyy", { locale: es }),
    horaCorta: format(d, 'HH:mm', { locale: es }),
    fechaHora: format(d, "dd 'de' MMM, yyyy 'a las' HH:mm", { locale: es }),
  };
};

const EntregasAlistadas = ({ entregas, isLoading, onMarcarEnCamino, onRefresh }) => {
  const [selectedEntregas, setSelectedEntregas] = useState([]);
  const [entregaDetalle, setEntregaDetalle] = useState(null);
  const [isDetalleOpen, setIsDetalleOpen] = useState(false);

  const handleToggleEntrega = (entregaId) => {
    setSelectedEntregas(prev => 
      prev.includes(entregaId)
        ? prev.filter(id => id !== entregaId)
        : [...prev, entregaId]
    );
  };

  const handleVerDetalle = (entrega, e) => {
    e.stopPropagation();
    setEntregaDetalle(entrega);
    setIsDetalleOpen(true);
  };

  const handleIniciarRuta = async () => {
    const entregasSeleccionadas = entregas.filter(e => selectedEntregas.includes(e._id));
    await onMarcarEnCamino(entregasSeleccionadas);
    setSelectedEntregas([]);
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-4">
              <div className="h-24 bg-gray-200 rounded" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (entregas.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <Package className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 mb-4">No hay entregas alistadas</p>
          <Button
            variant="outline"
            size="sm"
            onClick={onRefresh}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualizar
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {/* Botón flotante para iniciar ruta */}
      {selectedEntregas.length > 0 && (
        <div className="fixed bottom-20 left-0 right-0 p-4 bg-gradient-to-t from-white to-transparent pointer-events-none">
          <Button
            onClick={handleIniciarRuta}
            className="w-full bg-blue-600 hover:bg-blue-700 shadow-lg pointer-events-auto"
            size="lg"
          >
            <Truck className="h-5 w-5 mr-2" />
            Iniciar Ruta ({selectedEntregas.length})
          </Button>
        </div>
      )}

      {entregas.map((entrega) => {
        const isSelected = selectedEntregas.includes(entrega._id);
        const tipoIcon = entrega.tipo === 'lenceria' ? Package : Droplet;
        const TipoIcon = tipoIcon;

        return (
          <Card 
            key={entrega._id} 
            className={`
              hover:shadow-md transition-all
              ${isSelected ? 'ring-2 ring-blue-500 bg-blue-50' : ''}
            `}
          >
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                {/* Checkbox */}
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={() => handleToggleEntrega(entrega._id)}
                  className="mt-1"
                />

                {/* Contenido */}
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <TipoIcon className={`h-5 w-5 ${
                        entrega.tipo === 'lenceria' ? 'text-blue-600' : 'text-purple-600'
                      }`} />
                      <div>
                        <h3 className="font-semibold text-gray-900 text-sm">
                          {entrega.edificio?.nombre}
                        </h3>
                        {entrega.hospedaje?.nombre && (
                          <p className="text-xs text-gray-500">
                            {entrega.hospedaje.nombre}
                          </p>
                        )}
                      </div>
                    </div>
                    <Badge className="bg-blue-100 text-blue-700 text-xs">
                      Alistado
                    </Badge>
                  </div>

                  {/* Dirección */}
                  <div className="flex items-start gap-2 mb-3 text-xs text-gray-600">
                    <MapPin className="h-3 w-3 mt-0.5 flex-shrink-0" />
                    <span className="line-clamp-2">
                      {entrega.edificio?.direccion}, {entrega.edificio?.ciudad}
                    </span>
                  </div>

                  {/* Información */}
                  {(() => {
                    const prendasCount = entrega.items?.filter(i => i.tipo_item !== 'insumo').reduce((s, i) => s + i.cantidad, 0) || 0;
                    const insumosCount = entrega.items?.filter(i => i.tipo_item === 'insumo').reduce((s, i) => s + i.cantidad, 0) || 0;
                    return (
                      <div className={`grid ${insumosCount > 0 ? 'grid-cols-4' : 'grid-cols-3'} gap-2 text-xs mb-3`}>
                        <div className="bg-gray-50 rounded p-2 text-center">
                          <div className="text-gray-500">Items</div>
                          <div className="font-semibold text-gray-900">
                            {entrega.total_items || 0}
                          </div>
                        </div>
                        <div className="bg-gray-50 rounded p-2 text-center">
                          <div className="text-gray-500">Peso</div>
                          <div className="font-semibold text-gray-900">
                            {((entrega.peso_total || 0) / 1000).toFixed(1)} kg
                          </div>
                        </div>
                        <div className="bg-gray-50 rounded p-2 text-center">
                          <div className="text-gray-500">Tipo</div>
                          <div className="font-semibold text-gray-900 capitalize">
                            {entrega.tipo || 'N/A'}
                          </div>
                        </div>
                        {insumosCount > 0 && (
                          <div className="bg-amber-50 border border-amber-200 rounded p-2 text-center">
                            <div className="text-amber-600 flex items-center justify-center gap-1">
                              <Droplets className="h-3 w-3" />
                              Insumos
                            </div>
                            <div className="font-semibold text-amber-700">
                              {insumosCount}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Alistamiento: alistador + fecha/hora */}
                  {(() => {
                    const fechaAlistamiento = formatFechaAlistamiento(entrega);
                    return (
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-xs text-gray-400 min-w-0 flex-1">
                      {/*     <div className="truncate">
                            Alistado por: <span className="text-gray-600">{entrega.alistador?.nombre || 'N/A'}</span>
                          </div> */}
                          {fechaAlistamiento && (
                            <div className="flex items-center gap-1 mt-0.5 text-[11px] text-gray-500">
                              <Calendar className="h-3 w-3 flex-shrink-0" />
                              <span className="truncate">
                                {fechaAlistamiento.fechaCorta}
                                <span className="mx-1 text-gray-300">·</span>
                                <Clock className="h-3 w-3 inline-block -mt-0.5" />
                                {fechaAlistamiento.horaCorta}
                              </span>
                            </div>
                          )}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs flex-shrink-0"
                          onClick={(e) => handleVerDetalle(entrega, e)}
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          Ver Items
                        </Button>
                      </div>
                    );
                  })()}
                </div>

                {/* Botón individual */}
       {/*          <Button
                  variant="ghost"
                  size="icon"
                  className="flex-shrink-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    onMarcarEnCamino([entrega]);
                  }}
                >
                  <ChevronRight className="h-5 w-5" />
                </Button> */}
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* Sheet de detalle */}
      <Sheet open={isDetalleOpen} onOpenChange={setIsDetalleOpen}>
        <SheetContent side="bottom" className="h-[80vh]">
          <SheetHeader>
            <SheetTitle>Detalle de Entrega</SheetTitle>
            <SheetDescription>
              {entregaDetalle?.edificio?.nombre} - {entregaDetalle?.hospedaje?.nombre || 'Sin hospedaje'}
            </SheetDescription>
          </SheetHeader>

          {entregaDetalle && (
            <div className="mt-6 space-y-4 overflow-y-auto h-[calc(80vh-120px)]">
              {/* Información general */}
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Total Items:</span>
                  <span className="font-semibold">{entregaDetalle.total_items}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Peso Total:</span>
                  <span className="font-semibold">
                    {((entregaDetalle.peso_total || 0) / 1000).toFixed(2)} kg
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Tipo:</span>
                  <span className="font-semibold capitalize">{entregaDetalle.tipo}</span>
                </div>
{/*                 <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Alistado por:</span>
                  <span className="font-semibold">{entregaDetalle.alistador?.nombre}</span>
                </div> */}
                {(() => {
                  const fechaAlistamiento = formatFechaAlistamiento(entregaDetalle);
                  if (!fechaAlistamiento) return null;
                  return (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        Fecha alistamiento:
                      </span>
                      <span className="font-semibold text-right">
                        {fechaAlistamiento.fechaHora}
                      </span>
                    </div>
                  );
                })()}
              </div>

              {/* Lista de items */}
              <div>
                <h3 className="font-semibold mb-3">Items Alistados</h3>
                <div className="space-y-2">
                  {entregaDetalle.items?.map((item, index) => {
                    const isInsumo = item.tipo_item === 'insumo';
                    return (
                      <div
                        key={index}
                        className={`flex items-center justify-between p-3 border rounded-lg ${
                          isInsumo ? 'bg-amber-50 border-amber-200' : 'bg-white'
                        }`}
                      >
                        <div className="flex items-center gap-2 flex-1">
                          {isInsumo 
                            ? <Droplets className="h-4 w-4 text-amber-600 flex-shrink-0" />
                            : <Shirt className="h-4 w-4 text-blue-600 flex-shrink-0" />
                          }
                          <div>
                            <div className="font-medium text-sm">{item.nombre}</div>
                            {isInsumo ? (
                              <Badge variant="secondary" className="text-[10px] bg-amber-100 text-amber-700 border border-amber-300 mt-0.5">
                                Insumo
                              </Badge>
                            ) : item.peso ? (
                              <div className="text-xs text-gray-500">
                                Peso unitario: {item.peso}g
                              </div>
                            ) : null}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`font-semibold ${isInsumo ? 'text-amber-700' : 'text-blue-600'}`}>
                            x{item.cantidad}
                          </div>
                          {!isInsumo && item.peso && (
                            <div className="text-xs text-gray-500">
                              {((item.peso * item.cantidad) / 1000).toFixed(2)} kg
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Observaciones */}
              {entregaDetalle.observaciones && (
                <div>
                  <h3 className="font-semibold mb-2">Observaciones</h3>
                  <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
                    {entregaDetalle.observaciones}
                  </p>
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default EntregasAlistadas;
